import React, {useEffect, useState} from 'react';

import {
    FolioCredentialsFailingBar,
    StorefrontSubExtensionOverrideForm,
    StorefrontSubExtensionQuoteRequestOpportunityOverrideForm
} from "../ExtensionInstallation/ExtensionDataForm";
import {ActionButton, MessageBar, MessageBarType} from "@fluentui/react";
import {useGetExtensionFolioQuery} from "../../store/Api";
import {IStorefrontSettings} from "../../models/Storefront";
import ToggleField from "../Common/ToggleField";
import StorefrontOpportunityExtensionSelection from "./StorefrontOpportunityExtensionSelection";
import StorefrontOpportunityTokenReference from "./StorefrontOpportunityTokenReference";
import { DynamicSetting } from "../Common/Setting";

interface IStorefrontOpportunityExtensionForm {
    subExtensionFolioCompositeId?: string;
    quoteRequestSubExtensionFolioCompositeId?: string;
    settings: IStorefrontSettings;
    onChange?: (settings: IStorefrontSettings) => void;
    onLoading?: (isLoading: boolean) => void;
    showQuoteRequestSettings?: boolean;
    globalSalesOrderEnabled?: boolean;
    /**
     * Registry id prefix for the fields on this form (e.g. 'storefront.integrations.'). The form is
     * shared by the per-storefront and global integrations screens, so the hosting page decides
     * which registered ids (if any) its copy of the fields carries — same pattern as
     * styleControls' settingKeyPrefix.
     */
    settingIdPrefix?: string;
}

const StorefrontOpportunityExtensionForm: React.FC<IStorefrontOpportunityExtensionForm> = (props) => {
    const mark = (idSuffix: string, control: React.ReactElement) =>
        props.settingIdPrefix ? <DynamicSetting id={`${props.settingIdPrefix}${idSuffix}`}>{control}</DynamicSetting> : control;
    const [currentExtension, setCurrentExtension] = useState<string>();
    const [currentQuoteRequestExtension, setCurrentQuoteRequestExtension] = useState<string | undefined>();
    const [orderExpanded, setOrderExpanded] = useState(false);
    const [quoteExpanded, setQuoteExpanded] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [success, setSuccess] = useState<string | undefined>();

    // A broken connection must be visible at this level, not discovered behind "Show Details": the
    // selected extension is a folio sub-extension (composite folio_{source}_{sub} id), so the folio's
    // validation stamp says whether there is anything worth expanding. RTK de-duplicates this query
    // with the override forms' own fetches.
    const folioSourceOf = (compositeId?: string) => {
        const parts = compositeId?.split('_');
        return parts?.length === 3 ? parts[1] : undefined;
    };
    const orderFolioSource = folioSourceOf(currentExtension);
    const quoteFolioSource = folioSourceOf(currentQuoteRequestExtension);
    const orderFolio = useGetExtensionFolioQuery(orderFolioSource ?? '', {skip: !orderFolioSource});
    const quoteFolio = useGetExtensionFolioQuery(quoteFolioSource ?? '', {skip: !quoteFolioSource});
    const orderCredentialsFailing = orderFolio.data?.lastValidationSuccess === false;
    const quoteCredentialsFailing = quoteFolio.data?.lastValidationSuccess === false;
    
    useEffect(() => {
        if(props.settings.opportunityExtension){
            setCurrentExtension(props.settings.opportunityExtension);
        }
        else{
            setCurrentExtension(props.subExtensionFolioCompositeId);
        }
        
        if(props.settings.quoteRequestExtension){
            setCurrentQuoteRequestExtension(props.settings.quoteRequestExtension);
        }
        else{
            setCurrentQuoteRequestExtension(props.quoteRequestSubExtensionFolioCompositeId);
        }
    }, [props.settings, props.subExtensionFolioCompositeId, props.quoteRequestSubExtensionFolioCompositeId]);

    const detailsBoxStyle: React.CSSProperties = {
        border: '1px solid #edebe9',
        borderRadius: 8,
        padding: 16,
        marginTop: 8,
        marginRight: 16,
        background: '#fff'
    };

    // Inset card for the expanded ("Show Details") content, so it reads as a distinct sub-section separated from
    // the always-visible controls above: a full border (rounded) with a brand-green left accent and faint tint.
    const expandedDetailsStyle: React.CSSProperties = {
        marginTop: 10,
        padding: 14,
        border: '1px solid #edebe9',
        borderLeft: '3px solid #e2e9cb',
        borderRadius: 6,
        background: '#fbfcf8'
    };

    return (
        <>
            <>
                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> }
                {success && <MessageBar messageBarType={MessageBarType.success}>{success}</MessageBar> }
                <div style={detailsBoxStyle}>
                    <h3 style={{marginTop: 0}}>Order Opportunity Details</h3>

                    {/* Always visible */}
                    {mark('opportunityExtension', <StorefrontOpportunityExtensionSelection
                        extension={currentExtension}
                        onChange={(extension) => {
                            setCurrentExtension(extension);
                            props.onChange?.({...props.settings, opportunityExtension: extension});
                        }}
                    />)}
                    {mark('disableOpportunityCreation', <ToggleField
                        inline={true}
                        label="Disable Opportunity Creation"
                        checked={props.settings.disableOpportunityCreation}
                        onChange={(e, checked) => {
                            props.onChange?.(checked? {...props.settings, disableOpportunityCreation: true} : {...props.settings, disableOpportunityCreation: false});
                        }}
                    />)}

                    {/* A failing connection is announced here, where it is visible without expanding
                        anything, and there are no details to show: the override fields cannot save and
                        the token reference has nothing to feed. */}
                    {orderCredentialsFailing ?
                        <div style={{marginTop: 8}}>
                            <FolioCredentialsFailingBar folio={orderFolio.data} />
                        </div>
                        :
                    <>
                    {/* Expand control + advanced/expandable content */}
                    {(currentExtension || (props.globalSalesOrderEnabled && !props.settings.disableOpportunityCreation)) && <>
                        <ActionButton
                            iconProps={{iconName: orderExpanded ? 'Remove' : 'Add'}}
                            styles={{root: {paddingLeft: 0, marginTop: 4}}}
                            onClick={() => setOrderExpanded(!orderExpanded)}
                        >
                            {orderExpanded ? 'Hide Details' : 'Show Details'}
                        </ActionButton>
                        {orderExpanded && <div style={expandedDetailsStyle}>
                            {props.globalSalesOrderEnabled && !props.settings.disableOpportunityCreation &&
                                mark('disableSalesOrderCreation', <ToggleField
                                    inline={true}
                                    label="Disable Sales Order Creation"
                                    checked={props.settings.disableSalesOrderCreation}
                                    onChange={(e, checked) => {
                                        props.onChange?.({...props.settings, disableSalesOrderCreation: checked ?? false});
                                    }}
                                />)
                            }
                            {currentExtension && <>
                                <StorefrontSubExtensionOverrideForm
                                    extensionId={currentExtension}
                                    settings={props.settings}
                                    onChange={props.onChange}
                                    onLoading={props.onLoading}
                                />
                                <StorefrontOpportunityTokenReference settings={props.settings} mode="order" />
                            </>}
                        </div>}
                    </>}
                    </>}
                </div>
                {props.showQuoteRequestSettings &&
                    <div style={detailsBoxStyle}>
                        <h3 style={{marginTop: 0}}>Quote Request Opportunity Details</h3>

                        {/* Always visible */}
                        {mark('quoteRequestExtension', <StorefrontOpportunityExtensionSelection
                            extension={currentQuoteRequestExtension}
                            onChange={(extension) => {
                                setCurrentQuoteRequestExtension(extension);
                                props.onChange?.({...props.settings, quoteRequestExtension: extension});
                            }}
                        />)}
                        {mark('disableOpportunityCreationQuoteRequest', <ToggleField
                            inline={true}
                            label="Disable Opportunity Creation"
                            checked={props.settings.disableOpportunityCreationQuoteRequest}
                            onChange={(e, checked) => {
                                props.onChange?.(checked? {...props.settings, disableOpportunityCreationQuoteRequest: true} : {...props.settings, disableOpportunityCreationQuoteRequest: false});
                            }}
                        />)}

                        {/* Same as the order box: a broken connection is announced without expanding,
                            and the details it gates are not offered. */}
                        {quoteCredentialsFailing ?
                            <div style={{marginTop: 8}}>
                                <FolioCredentialsFailingBar folio={quoteFolio.data} />
                            </div>
                            :
                        <>
                        {/* Expand control + expandable content */}
                        {currentQuoteRequestExtension && <>
                            <ActionButton
                                iconProps={{iconName: quoteExpanded ? 'Remove' : 'Add'}}
                                styles={{root: {paddingLeft: 0, marginTop: 4}}}
                                onClick={() => setQuoteExpanded(!quoteExpanded)}
                            >
                                {quoteExpanded ? 'Hide Details' : 'Show Details'}
                            </ActionButton>
                            {quoteExpanded && <div style={expandedDetailsStyle}>
                                <StorefrontSubExtensionQuoteRequestOpportunityOverrideForm
                                    extensionId={currentQuoteRequestExtension}
                                    settings={props.settings}
                                    onChange={props.onChange}
                                    onLoading={props.onLoading}
                                />
                                <StorefrontOpportunityTokenReference settings={props.settings} mode="quoteRequest" />
                            </div>}
                        </>}
                        </>}
                    </div>
                }
        </>
        
        </>
    );
}

export default StorefrontOpportunityExtensionForm;